// 首页展示配置模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { HomeDisplayState, HomeDisplayActions } from "../types";
import { homeDisplayService } from "../services";
import { BaseCache } from "../../base/stores";
// ✅ 从独立的缓存工具文件导入，避免触发组件样式加载
import {
  setCachedDoubleBetFromSeconds,
  setCachedBetActivityFromSeconds,
  setCachedTomorrowFromSeconds,
} from "@/components/Event/components/BetActivity/BetActivity.cache";

// 创建首页展示配置模块专用的缓存实例
const homeDisplayCache = new BaseCache({
  defaultTTL: 5 * 60 * 1000, // 5分钟
  maxSize: 10,
  enableLogging: true,
});

// 初始状态
const initialState: HomeDisplayState = {
  data: null,
  loading: false,
  isConfigLoaded: false,
  cache: new Map(),
};

// 创建首页展示配置store
export const useHomeDisplayStore = createWithEqualityFn<
  HomeDisplayState & HomeDisplayActions
>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // 数据相关
    setData: config => set({ data: config, isConfigLoaded: config !== null }),
    setLoading: loading => set({ loading }),

    // 加载配置
    loadConfig: async () => {
      const { setLoading, setData } = get();

      try {
        setLoading(true);

        const cacheKey = "home_display_config";

        // 使用缓存优先的 API 调用
        const data = await homeDisplayCache.cacheFirst(
          cacheKey,
          () => homeDisplayService.loadConfig(),
          5 * 60 * 1000 // 5分钟缓存
        );

        setData(data);
        setLoading(false);
        return data;
      } catch (error) {
        console.error("首页展示配置加载失败:", error);
        setLoading(false);
        throw error;
      }
    },

    /** 强制重新请求 config（清缓存 + 绕过节流），用于关闭「次数过多」弹窗后拿到最新冷却状态 */
    refreshConfig: async () => {
      get().clearCache("home_display_config");
      return homeDisplayService.refreshConfig();
    },

    // 缓存相关 - 委托给 BaseCache 实例
    setCache: (key, data, ttl) => {
      homeDisplayCache.setCache(key, data, ttl);
      set({ cache: new Map(homeDisplayCache["cache"]) });
    },

    getCache: key => {
      return homeDisplayCache.getCache(key);
    },

    clearCache: pattern => {
      homeDisplayCache.clearCache(pattern);
      set({ cache: new Map(homeDisplayCache["cache"]) });
    },

    hasCache: key => {
      return homeDisplayCache.hasCache(key);
    },

    getCacheKeys: () => {
      return homeDisplayCache.getCacheKeys();
    },

    getCacheSize: () => {
      return homeDisplayCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return homeDisplayCache.cacheFirst(key, apiCall, ttl);
    },
  }))
);

// ===========================
// 基础选择器
// ===========================

/**
 * 获取首页展示数据和加载状态
 */
export const useHomeDisplayData = () =>
  useHomeDisplayStore(state => ({
    config: state.data,
    loading: state.loading,
  }));

/**
 * 获取首页展示配置是否已加载完成
 */
export const useIsHomeDisplayReady = () =>
  useHomeDisplayStore(state => state.isConfigLoaded);

// ===========================
// 服务 Hooks
// ===========================

/**
 * 服务方法 hook
 */
export function useHomeDisplayService() {
  return useHomeDisplayStore(state => ({
    loadConfig: state.loadConfig,
    refreshConfig: state.refreshConfig,
    setData: state.setData,
    setLoading: state.setLoading,
  }));
}

// 缓存 hook
export function useHomeDisplayCache() {
  return useHomeDisplayStore(state => ({
    setCache: state.setCache,
    getCache: state.getCache,
    clearCache: state.clearCache,
    hasCache: state.hasCache,
    getCacheKeys: state.getCacheKeys,
    getCacheSize: state.getCacheSize,
    cacheFirst: state.cacheFirst,
  }));
}

// ===========================
// BET_TASK 数据订阅 - 自动更新倒计时缓存
// ===========================

/**
 * 订阅 BET_TASK 数据变化，自动更新 localStorage 缓存
 * 该订阅在模块加载时自动执行
 */
useHomeDisplayStore.subscribe(
  state => state.data?.activities?.BET_TASK?.list?.[0]?.betData,
  betData => {
    if (!betData) return;

    // 1. 更新 bet 加速模式结束时间缓存
    if (
      betData.balanceWarningExpireSeconds &&
      betData.balanceWarningExpireSeconds > 0
    ) {
      setCachedDoubleBetFromSeconds(betData.balanceWarningExpireSeconds);
    }

    // 2. 更新普通 bet 活动结束时间缓存
    if (betData.countdownSeconds && betData.countdownSeconds > 0) {
      setCachedBetActivityFromSeconds(betData.countdownSeconds);
    }

    // 3. 更新明天0点倒计时缓存
    if (
      betData.tomorrowCountdownSeconds &&
      betData.tomorrowCountdownSeconds > 0
    ) {
      setCachedTomorrowFromSeconds(betData.tomorrowCountdownSeconds);
    }
  }
);
